import {
    createSkill,
    deleteSkill,
    duplicateSkill,
    getSkill,
    listSkills,
    loadAndRenderSkills as loadAndRenderSkillsApi,
    replaceProjectSkills,
    saveSkillContent as saveSkillContentApi,
    saveProjectSkills as saveProjectSkillsApi,
    selectSkill as selectSkillApi,
    updateSkill
} from "../api-client/skills-api.js";

const SKILL_CATEGORY_ORDER = [
    "I/O標準",
    "異常処理",
    "モーション制御",
    "命名規則",
    "ワークスペース"
];

const DEFAULT_CUSTOM_SKILL_CONTENT = `---
name: 新規カスタムSkill
description: このSkillの目的をここに書いてください
---
# 新規カスタムSkill

- このSkillで守りたい方針や命名規則を書く
- 必要なI/O、制約、禁止事項を書く
`;

function defaultEscapeHtml(value) {
    if (typeof value !== "string") return "";
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function formatSkillTimestamp(value) {
    if (typeof value !== "string" || !value.trim()) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
}

export function createSkillsUiController(deps = {}) {
    const {
        getRefs = () => ({}),
        getAccessToken = async () => null,
        getProject = () => null,
        getSkillListItems = () => document.querySelectorAll(".skill-item"),
        createElement = (tagName) => document.createElement(tagName),
        escapeHtml = defaultEscapeHtml,
        syncAssistantState = () => {},
        setTimeoutFn = (fn, delay) => setTimeout(fn, delay),
        confirmFn = (message) => window.confirm(message),
        error = () => {}
    } = deps;

    let currentSkills = [];
    let selectedSkillId = null;
    let sidebarTab = "catalog";
    let catalogTab = "builtin";
    let catalogSearchQuery = "";
    let catalogSortMode = "nameAsc";
    let openCatalogMenuState = null;

    function setHidden(element, hidden) {
        if (!element) return;
        element.hidden = Boolean(hidden);
    }

    function categoryRank(category) {
        const index = SKILL_CATEGORY_ORDER.indexOf(category);
        return index >= 0 ? index : SKILL_CATEGORY_ORDER.length;
    }

    function groupSkillsByCategory(skills) {
        const buckets = new Map();
        skills.forEach((skill) => {
            const category = String(skill?.category || "ワークスペース").trim() || "ワークスペース";
            if (!buckets.has(category)) {
                buckets.set(category, []);
            }
            buckets.get(category).push(skill);
        });

        return [...buckets.entries()]
            .sort((a, b) => {
                const rankDiff = categoryRank(a[0]) - categoryRank(b[0]);
                if (rankDiff !== 0) return rankDiff;
                return a[0].localeCompare(b[0], "ja");
            })
            .map(([category, skillsInCategory]) => ({
                category,
                skills: sortCatalogSkills(skillsInCategory)
            }));
    }

    function normalizeCatalogSearch(value) {
        return String(value || "").trim().toLowerCase();
    }

    function compareSkillNames(a, b) {
        return String(a?.name || "").localeCompare(String(b?.name || ""), "ja");
    }

    function compareSkillUpdatedAt(a, b) {
        const left = Date.parse(a?.updatedAt || "") || 0;
        const right = Date.parse(b?.updatedAt || "") || 0;
        return left - right;
    }

    function sortCatalogSkills(skills) {
        const list = Array.isArray(skills) ? skills.slice() : [];
        list.sort((a, b) => {
            if (catalogSortMode === "nameDesc") {
                return compareSkillNames(b, a);
            }
            if (catalogSortMode === "updatedDesc") {
                const diff = compareSkillUpdatedAt(b, a);
                return diff !== 0 ? diff : compareSkillNames(a, b);
            }
            if (catalogSortMode === "updatedAsc") {
                const diff = compareSkillUpdatedAt(a, b);
                return diff !== 0 ? diff : compareSkillNames(a, b);
            }
            return compareSkillNames(a, b);
        });
        return list;
    }

    function filterCatalogSkills(skills) {
        const query = normalizeCatalogSearch(catalogSearchQuery);
        if (!query) {
            return Array.isArray(skills) ? skills.slice() : [];
        }
        return (Array.isArray(skills) ? skills : []).filter((skill) => {
            const haystack = [
                skill?.name || "",
                skill?.description || "",
                skill?.category || "",
                skill?.status || "",
                skill?.visibility || ""
            ].join("\n").toLowerCase();
            return haystack.includes(query);
        });
    }

    function getDownloadedSkills() {
        const projectSkillIds = Array.isArray(getProject()?.skillIds) ? getProject().skillIds : [];
        const projectSkillIdSet = new Set(projectSkillIds);
        return currentSkills.filter((skill) => projectSkillIdSet.has(skill.id));
    }

    function getBuiltinSkills() {
        return currentSkills.filter((skill) => skill?.isBuiltin);
    }

    function getCustomSkills() {
        return currentSkills.filter((skill) => !skill?.isBuiltin);
    }

    function getOwnCustomSkills() {
        return getCustomSkills().filter((skill) => skill?.visibility !== "shared");
    }

    function getSharedCustomSkills() {
        return getCustomSkills().filter((skill) => skill?.visibility === "shared");
    }

    function getSelectedSkill() {
        return currentSkills.find((skill) => skill.id === selectedSkillId) || null;
    }

    function renderSelectedSkillMetadata(skill = null) {
        const refs = getRefs();
        if (refs.skillCreatedByNote) {
            refs.skillCreatedByNote.textContent = `作成者: ${skill?.createdByName || "-"}`;
        }
        if (refs.skillUpdatedByNote) {
            refs.skillUpdatedByNote.textContent = `更新者: ${skill?.updatedByName || "-"}`;
        }
        if (refs.skillUpdatedAtNote) {
            refs.skillUpdatedAtNote.textContent = `更新日時: ${formatSkillTimestamp(skill?.updatedAt || "")}`;
        }
    }

    function closeCatalogMenu() {
        const state = openCatalogMenuState;
        if (!state) {
            return false;
        }
        state.item?.classList?.toggle?.("is-menu-open", false);
        if (state.menu?.remove) {
            state.menu.remove();
        } else if (state.menu?.parentNode?.removeChild) {
            state.menu.parentNode.removeChild(state.menu);
        }
        openCatalogMenuState = null;
        return true;
    }

    function openCatalogMenu(item, anchor, actions) {
        if (!item || !anchor || !Array.isArray(actions) || actions.length === 0 || typeof document === "undefined") {
            return false;
        }
        const isSameItem = openCatalogMenuState?.item === item;
        if (isSameItem) {
            closeCatalogMenu();
            return false;
        }
        closeCatalogMenu();

        const menu = createElement("div");
        menu.className = "skill-catalog-floating-menu";
        menu.addEventListener?.("click", (event) => {
            event.stopPropagation?.();
        });

        actions.forEach((action) => {
            const button = createElement("button");
            button.type = "button";
            button.className = `skill-catalog-menu-action${action.className ? ` ${action.className}` : ""}`;
            button.textContent = action.label;
            if (action.disabled) {
                button.disabled = true;
            } else {
                button.addEventListener?.("click", (event) => {
                    event.preventDefault?.();
                    event.stopPropagation?.();
                    closeCatalogMenu();
                    action.onClick?.();
                });
            }
            menu.appendChild(button);
        });

        document.body?.appendChild?.(menu);
        item.classList?.toggle?.("is-menu-open", true);

        const rect = anchor.getBoundingClientRect?.();
        const menuRect = menu.getBoundingClientRect?.();
        if (!rect || !menuRect) {
            openCatalogMenuState = { item, menu };
            return true;
        }

        const gap = 6;
        const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
        const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
        let left = rect.right - menuRect.width;
        left = Math.max(8, Math.min(left, Math.max(8, viewportWidth - menuRect.width - 8)));

        let top = rect.bottom + gap;
        if (top + menuRect.height > viewportHeight - 8) {
            top = rect.top - menuRect.height - gap;
        }
        top = Math.max(8, Math.min(top, Math.max(8, viewportHeight - menuRect.height - 8)));

        menu.style.position = "fixed";
        menu.style.left = `${left}px`;
        menu.style.top = `${top}px`;

        openCatalogMenuState = { item, menu };
        return true;
    }

    function renderSidebarTabs() {
        const refs = getRefs();
        const isCatalog = sidebarTab === "catalog";
        setHidden(refs.skillsSidebarCatalogPanel, !isCatalog);
        setHidden(refs.skillsSidebarProjectPanel, isCatalog);
        refs.btnSkillsSidebarTabCatalog?.classList?.toggle?.("is-active", isCatalog);
        refs.btnSkillsSidebarTabCatalog?.setAttribute?.("aria-selected", String(isCatalog));
        refs.btnSkillsSidebarTabProject?.classList?.toggle?.("is-active", !isCatalog);
        refs.btnSkillsSidebarTabProject?.setAttribute?.("aria-selected", String(!isCatalog));
        return true;
    }

    function setSidebarTab(nextTab) {
        sidebarTab = nextTab === "project" ? "project" : "catalog";
        renderSidebarTabs();
        return true;
    }

    function updateSelectedSkillActions() {
        const refs = getRefs();
        const selectedSkill = getSelectedSkill();
        const isCustomSkill = Boolean(selectedSkill && !selectedSkill.isBuiltin);
        if (refs.btnDeleteSkill) {
            refs.btnDeleteSkill.disabled = !isCustomSkill;
            refs.btnDeleteSkill.title = !isCustomSkill
                ? "カスタムSkillを選択すると削除できます。"
                : "選択中のカスタムSkillを削除します。";
        }
    }

    function resetSelectedSkillEditor() {
        const refs = getRefs();
        if (refs.skillsEditorTitle) refs.skillsEditorTitle.textContent = "SKILL.md エディタ";
        if (refs.skillNameInput) refs.skillNameInput.value = "";
        if (refs.skillDescriptionInput) refs.skillDescriptionInput.value = "";
        if (refs.skillStatusSelect) refs.skillStatusSelect.value = "draft";
        if (refs.skillVisibilityNote) refs.skillVisibilityNote.textContent = "公開範囲: private";
        if (refs.skillContentEditor) {
            refs.skillContentEditor.value = "一覧からSkillを選択すると、ここにSKILL.md本文が表示されます。";
        }
        renderSelectedSkillMetadata(null);
    }

    function renderSkillsList() {
        const refs = getRefs();
        if (!refs.skillsListContainer) return false;

        const downloadedSkills = getDownloadedSkills();
        if (downloadedSkills.length === 0) {
            refs.skillsListContainer.innerHTML = '<p class="help">まだ追加済みのSkillがありません。上のSkill一覧から追加してください。</p>';
            return true;
        }

        refs.skillsListContainer.innerHTML = "";
        downloadedSkills.forEach((skill) => {
            const item = createElement("div");
            item.className = "skill-item";
            if (item.dataset) {
                item.dataset.skillId = skill.id;
            } else {
                item.dataset = { skillId: skill.id };
            }
            if (selectedSkillId === skill.id) {
                item.classList.add("is-selected");
            }

            item.innerHTML = `
            <div class="skill-item-main">
                <div class="skill-item-info">
                    <div class="skill-item-meta">${escapeHtml(skill.category || "Skill")}</div>
                    <div class="skill-item-name">${escapeHtml(skill.name)}</div>
                    <div class="skill-item-desc">${escapeHtml(skill.description || "")}</div>
                </div>
            </div>
        `;

            item.addEventListener("click", () => {
                void selectSkill(skill.id);
            });

            refs.skillsListContainer.appendChild(item);
        });

        updateSelectedSkillActions();
        return true;
    }

    function buildCatalogItem(skill, projectSkillIds) {
        const item = createElement("div");
        item.className = "skill-catalog-item";
        if (item.dataset) {
            item.dataset.skillId = skill.id;
        } else {
            item.dataset = { skillId: skill.id };
        }

        const isEnabled = projectSkillIds.includes(skill.id);
        const isCustomSkill = !skill.isBuiltin;
        const isSharedCustomSkill = isCustomSkill && skill.visibility === "shared";
        const meta = [];
        if (!skill.isBuiltin) {
            meta.push(skill.status === "published" ? "published" : "draft");
            meta.push(skill.visibility === "shared" ? "shared" : "private");
        }
        const actionButtons = [
            `<button type="button" class="${isEnabled ? "secondary" : "primary"} skills-soft-button skill-catalog-add-btn" data-skill-id="${escapeHtml(skill.id)}" ${isEnabled ? "disabled" : ""}>${isEnabled ? "追加済み" : "追加"}</button>`
        ];
        const menuActions = [];
        if (isCustomSkill) {
            if (isSharedCustomSkill) {
                menuActions.push({
                    label: "複製",
                    className: "skill-catalog-duplicate-btn",
                    onClick: () => {
                        void duplicateSkillToPrivate(skill.id);
                    }
                });
                menuActions.push({
                    label: "自分専用に戻す",
                    className: "skill-catalog-unshare-btn",
                    onClick: () => {
                        void unshareSkillToPrivate(skill.id);
                    }
                });
            } else {
                menuActions.push({
                    label: "共有",
                    className: "skill-catalog-share-btn",
                    onClick: () => {
                        void shareSkillToOrg(skill.id);
                    }
                });
            }
        }
        item.innerHTML = `
                <div class="skill-item-main">
                    <div class="skill-item-info">
                        <div class="skill-item-name">${escapeHtml(skill.name)}</div>
                        <div class="skill-item-desc">${escapeHtml(skill.description || "")}</div>
                        ${meta.length > 0 ? `<div class="skill-item-meta">${escapeHtml(meta.join(" / "))}</div>` : ""}
                    </div>
                    <div class="skill-catalog-actions">
                        ${actionButtons.join("")}
                        ${menuActions.length > 0 ? `
                            <div class="skill-catalog-menu-wrap">
                                <button type="button" class="secondary skills-soft-button skill-catalog-menu-btn" aria-label="その他の操作" title="その他の操作" data-skill-id="${escapeHtml(skill.id)}">&#8942;</button>
                            </div>
                        ` : ""}
                    </div>
                </div>
            `;

        item.addEventListener("click", (event) => {
            if (event.target?.classList?.contains("skill-catalog-add-btn")) {
                event.preventDefault?.();
                event.stopPropagation?.();
                void downloadSkill(skill.id);
                return;
            }
            if (event.target?.classList?.contains("skill-catalog-menu-btn")) {
                event.preventDefault?.();
                event.stopPropagation?.();
                openCatalogMenu(item, event.target, menuActions);
                return;
            }
            closeCatalogMenu();
            void selectSkill(skill.id);
        });

        return item;
    }

    function renderBuiltinSkillsCatalogList() {
        const refs = getRefs();
        if (!refs.builtinSkillsCatalogListContainer) return false;

        const builtinSkills = getBuiltinSkills();
        const filteredBuiltinSkills = filterCatalogSkills(builtinSkills);
        if (filteredBuiltinSkills.length === 0) {
            refs.builtinSkillsCatalogListContainer.innerHTML = '<p class="help">利用可能な標準Skillがありません。</p>';
            return true;
        }

        refs.builtinSkillsCatalogListContainer.innerHTML = "";
        const projectSkillIds = Array.isArray(getProject()?.skillIds) ? getProject().skillIds : [];
        const groups = groupSkillsByCategory(filteredBuiltinSkills);

        groups.forEach(({ category, skills }) => {
            const section = createElement("section");
            section.className = "skill-category-section";

            const title = createElement("div");
            title.className = "skill-category-title";
            title.textContent = category;
            section.appendChild(title);

            const list = createElement("div");
            list.className = "skill-category-list";

            skills.forEach((skill) => {
                list.appendChild(buildCatalogItem(skill, projectSkillIds));
            });

            section.appendChild(list);
            refs.builtinSkillsCatalogListContainer.appendChild(section);
        });

        return true;
    }

    function renderCustomSkillsCatalogList() {
        const refs = getRefs();
        if (!refs.customOwnSkillsListContainer || !refs.customSharedSkillsListContainer) return false;

        const projectSkillIds = Array.isArray(getProject()?.skillIds) ? getProject().skillIds : [];
        const ownCustomSkills = sortCatalogSkills(filterCatalogSkills(getOwnCustomSkills()));
        const sharedCustomSkills = sortCatalogSkills(filterCatalogSkills(getSharedCustomSkills()));

        if (ownCustomSkills.length === 0) {
            refs.customOwnSkillsListContainer.innerHTML = '<p class="help">まだカスタムSkillがありません。</p>';
        } else {
            refs.customOwnSkillsListContainer.innerHTML = "";
            ownCustomSkills.forEach((skill) => {
                refs.customOwnSkillsListContainer.appendChild(buildCatalogItem(skill, projectSkillIds));
            });
        }

        if (sharedCustomSkills.length === 0) {
            refs.customSharedSkillsListContainer.innerHTML = '<p class="help">まだ利用できません。</p>';
        } else {
            refs.customSharedSkillsListContainer.innerHTML = "";
            sharedCustomSkills.forEach((skill) => {
                refs.customSharedSkillsListContainer.appendChild(buildCatalogItem(skill, projectSkillIds));
            });
        }

        if (refs.customSharedSkillsHelp) {
            refs.customSharedSkillsHelp.textContent = sharedCustomSkills.length > 0
                ? "共有中のSkillです。内容を確認してこのプロジェクトに追加できます。"
                : "組織に共有したSkillがここに表示されます。";
        }
        return true;
    }

    function renderSkillsCatalogList() {
        const refs = getRefs();
        if (!refs.skillsCatalogBuiltinPanel || !refs.skillsCatalogCustomPanel) return false;
        setHidden(refs.skillsCatalogBuiltinPanel, catalogTab !== "builtin");
        setHidden(refs.skillsCatalogCustomPanel, catalogTab !== "custom");
        refs.btnSkillsCatalogTabBuiltin?.classList?.toggle?.("is-active", catalogTab === "builtin");
        refs.btnSkillsCatalogTabBuiltin?.setAttribute?.("aria-selected", String(catalogTab === "builtin"));
        refs.btnSkillsCatalogTabCustom?.classList?.toggle?.("is-active", catalogTab === "custom");
        refs.btnSkillsCatalogTabCustom?.setAttribute?.("aria-selected", String(catalogTab === "custom"));
        renderBuiltinSkillsCatalogList();
        renderCustomSkillsCatalogList();
        return true;
    }

    function setCatalogTab(nextTab) {
        catalogTab = nextTab === "custom" ? "custom" : "builtin";
        renderSkillsCatalogList();
        return true;
    }

    function setCatalogSearchQuery(nextQuery) {
        catalogSearchQuery = String(nextQuery || "");
        renderSkillsCatalogList();
        return true;
    }

    function setCatalogSortMode(nextMode) {
        catalogSortMode = typeof nextMode === "string" && nextMode.trim() ? nextMode : "nameAsc";
        renderSkillsCatalogList();
        return true;
    }

    async function loadAndRenderSkills() {
        return loadAndRenderSkillsApi({
            refs: getRefs(),
            getAccessToken,
            listSkillsImpl: (params) => listSkills({ ...params, kind: "all" }),
            setCurrentSkills: (skills) => {
                currentSkills = skills;
            },
            renderSkillsList: () => {
                renderSkillsList();
                renderSkillsCatalogList();
                updateSelectedSkillActions();
            },
            error
        });
    }

    async function selectSkill(skillId) {
        const result = await selectSkillApi({
            skillId,
            setSelectedSkillId: (value) => {
                selectedSkillId = value;
            },
            skillItems: getSkillListItems(),
            refs: getRefs(),
            currentSkills,
            getAccessToken,
            getSkillImpl: getSkill,
            onLoaded: (detail) => {
                renderSelectedSkillMetadata(detail);
            },
            error
        });
        updateSelectedSkillActions();
        return result;
    }

    async function saveProjectSkills() {
        const refs = getRefs();
        const project = getProject() || {};
        const selectedSkillIds = Array.isArray(project.skillIds) ? [...project.skillIds] : [];

        return saveProjectSkillsApi({
            refs,
            projectId: project.id || "",
            selectedSkillIds,
            getAccessToken,
            replaceProjectSkillsImpl: replaceProjectSkills,
            applySavedSkillIds: (savedSkillIds) => {
                project.skillIds = savedSkillIds;
            },
            syncAssistantState,
            setTimeoutFn,
            error
        });
    }

    async function downloadSkill(skillId) {
        const project = getProject() || {};
        const currentSkillIds = Array.isArray(project.skillIds) ? [...project.skillIds] : [];
        if (currentSkillIds.includes(skillId)) {
            return true;
        }

        const nextSkillIds = [...currentSkillIds, skillId];
        const selectedSkill = currentSkills.find((skill) => skill.id === skillId) || null;
        const selectedSkillKey = selectedSkill?.key || null;

        const saved = await saveProjectSkillsApi({
            refs: getRefs(),
            projectId: project.id || "",
            selectedSkillIds: nextSkillIds,
            getAccessToken,
            replaceProjectSkillsImpl: replaceProjectSkills,
            applySavedSkillIds: (savedSkillIds) => {
                project.skillIds = savedSkillIds;
            },
            syncAssistantState,
            setTimeoutFn,
            error
        });

        if (!saved) {
            return false;
        }

        await loadAndRenderSkills();

        const nextSelectedSkill = currentSkills.find((skill) => skill.key === selectedSkillKey);
        if (nextSelectedSkill?.id) {
            await selectSkill(nextSelectedSkill.id);
        }

        return true;
    }

    async function applySelectedSkillToProject() {
        const selectedSkill = getSelectedSkill();
        if (!selectedSkill || selectedSkill.isBuiltin) {
            return false;
        }
        return downloadSkill(selectedSkill.id);
    }

    function setCatalogStatus(message, color = "") {
        const refs = getRefs();
        if (!refs.customSkillsStatus) {
            return false;
        }
        refs.customSkillsStatus.textContent = message || "";
        refs.customSkillsStatus.style.color = color;
        return true;
    }

    async function shareSkillToOrg(skillId) {
        if (!skillId) {
            return false;
        }
        setCatalogStatus("共有中...");
        try {
            let accessToken = null;
            try {
                accessToken = await getAccessToken();
            } catch {
                accessToken = null;
            }

            const saved = await updateSkill({
                accessToken,
                skillId,
                visibility: "shared",
                status: "published"
            });
            await loadAndRenderSkills();
            renderSkillsCatalogList();
            if (saved?.id) {
                await selectSkill(saved.id);
            }
            setCatalogStatus("共有しました。", "green");
            return true;
        } catch (err) {
            error("Failed to share skill", err);
            setCatalogStatus(`共有に失敗しました: ${err.message || err}`, "red");
            return false;
        }
    }

    async function duplicateSelectedSkillToPrivate() {
        return duplicateSkillToPrivate(selectedSkillId);
    }

    async function duplicateSkillToPrivate(skillId) {
        const refs = getRefs();
        const selectedSkill = currentSkills.find((skill) => skill.id === skillId) || null;
        if (!selectedSkill || selectedSkill.isBuiltin || selectedSkill.visibility !== "shared") {
            return false;
        }

        if (refs.skillEditorStatus) {
            refs.skillEditorStatus.textContent = "自分用Skillを複製中...";
            refs.skillEditorStatus.style.color = "";
        }

        try {
            let accessToken = null;
            try {
                accessToken = await getAccessToken();
            } catch {
                accessToken = null;
            }

            const duplicated = await duplicateSkill({
                accessToken,
                skillId: selectedSkill.id
            });
            await loadAndRenderSkills();
            if (duplicated?.id) {
                await selectSkill(duplicated.id);
            }
            if (refs.skillEditorStatus) {
                refs.skillEditorStatus.textContent = "自分用の private draft を作成しました。";
                refs.skillEditorStatus.style.color = "green";
            }
            return true;
        } catch (err) {
            error("Failed to duplicate skill", err);
            if (refs.skillEditorStatus) {
                refs.skillEditorStatus.textContent = `複製に失敗しました: ${err.message || err}`;
                refs.skillEditorStatus.style.color = "red";
            }
            return false;
        }
    }

    async function unshareSkillToPrivate(skillId) {
        if (!skillId) {
            return false;
        }
        setCatalogStatus("自分専用に戻しています...");
        try {
            let accessToken = null;
            try {
                accessToken = await getAccessToken();
            } catch {
                accessToken = null;
            }

            const saved = await updateSkill({
                accessToken,
                skillId,
                visibility: "private",
                status: "draft"
            });
            await loadAndRenderSkills();
            renderSkillsCatalogList();
            if (saved?.id) {
                await selectSkill(saved.id);
            }
            setCatalogStatus("自分専用に戻しました。", "green");
            return true;
        } catch (err) {
            error("Failed to unshare skill", err);
            setCatalogStatus(`自分専用に戻せませんでした: ${err.message || err}`, "red");
            return false;
        }
    }

    async function unshareSelectedSkillToPrivate() {
        const selectedSkill = getSelectedSkill();
        if (!selectedSkill || selectedSkill.isBuiltin || selectedSkill.visibility !== "shared") {
            return false;
        }
        return unshareSkillToPrivate(selectedSkill.id);
    }

    function buildDeleteImpactMessage(impactedProjects = []) {
        if (!Array.isArray(impactedProjects) || impactedProjects.length === 0) {
            return "";
        }
        const lines = impactedProjects.map((project) => `- ${project.name || project.id}`);
        return [
            "このSkillは他の project でも使用中です。",
            ...lines,
            "",
            "削除すると、それらの project からも外れます。続行しますか？"
        ].join("\n");
    }

    async function deleteSelectedSkill({ force = false } = {}) {
        const refs = getRefs();
        const selectedSkill = getSelectedSkill();
        if (!selectedSkill || selectedSkill.isBuiltin) {
            return false;
        }

        if (refs.skillEditorStatus) {
            refs.skillEditorStatus.textContent = "Skillを削除中...";
            refs.skillEditorStatus.style.color = "";
        }

        try {
            let accessToken = null;
            try {
                accessToken = await getAccessToken();
            } catch {
                accessToken = null;
            }

            await deleteSkill({
                accessToken,
                skillId: selectedSkill.id,
                force
            });
            const project = getProject() || {};
            if (Array.isArray(project.skillIds)) {
                const nextSkillIds = project.skillIds.filter((skillId) => skillId !== selectedSkill.id);
                project.skillIds = nextSkillIds;
                syncAssistantState(nextSkillIds);
            }
            selectedSkillId = null;
            resetSelectedSkillEditor();
            await loadAndRenderSkills();
            if (refs.skillEditorStatus) {
                refs.skillEditorStatus.textContent = "Skillを削除しました。";
                refs.skillEditorStatus.style.color = "green";
            }
            return true;
        } catch (err) {
            if (err?.status === 409) {
                const impactedProjects = Array.isArray(err?.details?.impactedProjects)
                    ? err.details.impactedProjects
                    : [];
                const confirmed = confirmFn(buildDeleteImpactMessage(impactedProjects));
                if (confirmed) {
                    return deleteSelectedSkill({ force: true });
                }
                if (refs.skillEditorStatus) {
                    refs.skillEditorStatus.textContent = "削除を中止しました。";
                    refs.skillEditorStatus.style.color = "#b45309";
                }
                return false;
            }
            error("Failed to delete skill", err);
            if (refs.skillEditorStatus) {
                refs.skillEditorStatus.textContent = `削除に失敗しました: ${err.message || err}`;
                refs.skillEditorStatus.style.color = "red";
            }
            return false;
        }
    }

    async function saveSelectedSkillContent() {
        const refs = getRefs();
        const content = typeof refs.skillContentEditor?.value === "string"
            ? refs.skillContentEditor.value
            : "";
        const name = typeof refs.skillNameInput?.value === "string"
            ? refs.skillNameInput.value
            : "";
        const description = typeof refs.skillDescriptionInput?.value === "string"
            ? refs.skillDescriptionInput.value
            : "";
        const status = typeof refs.skillStatusSelect?.value === "string"
            ? refs.skillStatusSelect.value
            : "draft";

        return saveSkillContentApi({
            refs,
            skillId: selectedSkillId || "",
            content,
            name,
            description,
            status,
            getAccessToken,
            updateSkillImpl: updateSkill,
            onSaved: (savedSkill) => {
                if (!savedSkill?.id) return;
                currentSkills = currentSkills.map((skill) => (
                    skill.id === selectedSkillId ? { ...skill, ...savedSkill } : skill
                ));
                selectedSkillId = savedSkill.id;
                renderSkillsList();
                renderSkillsCatalogList();
                updateSelectedSkillActions();
                if (refs.skillsEditorTitle) {
                    refs.skillsEditorTitle.textContent = `SKILL.md エディタ - ${savedSkill.name || name}`;
                }
                if (refs.skillVisibilityNote) {
                    refs.skillVisibilityNote.textContent = `公開範囲: ${savedSkill.visibility || "private"}`;
                }
                renderSelectedSkillMetadata(savedSkill);
            },
            error
        });
    }

    async function createCustomSkill() {
        const refs = getRefs();
        if (refs.customSkillsStatus) {
            refs.customSkillsStatus.textContent = "カスタムSkillを作成中...";
            refs.customSkillsStatus.style.color = "";
        }

        try {
            let accessToken = null;
            try {
                accessToken = await getAccessToken();
            } catch {
                accessToken = null;
            }

            const created = await createSkill({
                accessToken,
                name: "新規カスタムSkill",
                description: "",
                content: DEFAULT_CUSTOM_SKILL_CONTENT,
                visibility: "private",
                status: "draft"
            });
            await loadAndRenderSkills();
            setCatalogTab("custom");
            if (refs.customSkillsStatus) {
                refs.customSkillsStatus.textContent = "カスタムSkillを作成しました。";
                refs.customSkillsStatus.style.color = "green";
            }
            if (created?.id) {
                await selectSkill(created.id);
            }
            return true;
        } catch (err) {
            error("Failed to create custom skill", err);
            if (refs.customSkillsStatus) {
                refs.customSkillsStatus.textContent = `カスタムSkillの作成に失敗しました: ${err.message || err}`;
                refs.customSkillsStatus.style.color = "red";
            }
            return false;
        }
    }

    function initializeSkillsUi() {
        const refs = getRefs();
        refs.btnReloadSkills?.addEventListener("click", () => {
            void loadAndRenderSkills();
        });
        refs.btnSkillsSidebarTabCatalog?.addEventListener("click", () => {
            setSidebarTab("catalog");
        });
        refs.btnSkillsSidebarTabProject?.addEventListener("click", () => {
            setSidebarTab("project");
        });
        refs.btnSkillsCatalogTabBuiltin?.addEventListener("click", () => {
            setCatalogTab("builtin");
        });
        refs.btnSkillsCatalogTabCustom?.addEventListener("click", () => {
            setCatalogTab("custom");
        });
        refs.skillCatalogSearchInput?.addEventListener("input", () => {
            setCatalogSearchQuery(refs.skillCatalogSearchInput?.value || "");
        });
        refs.skillCatalogSortSelect?.addEventListener("change", () => {
            setCatalogSortMode(refs.skillCatalogSortSelect?.value || "nameAsc");
        });
        refs.btnCreateCustomSkill?.addEventListener("click", () => {
            void createCustomSkill();
        });
        refs.btnSaveSkillContent?.addEventListener("click", () => {
            void saveSelectedSkillContent();
        });
        refs.btnDeleteSkill?.addEventListener("click", () => {
            void deleteSelectedSkill();
        });
        if (typeof document !== "undefined") {
            document.addEventListener("click", () => {
                closeCatalogMenu();
            });
        }
        renderSidebarTabs();
        renderSelectedSkillMetadata(null);
        updateSelectedSkillActions();
        return true;
    }

    return {
        initializeSkillsUi,
        loadAndRenderSkills,
        renderSkillsList,
        renderSkillsCatalogList,
        setSidebarTab,
        setCatalogTab,
        createCustomSkill,
        downloadSkill,
        setCatalogSearchQuery,
        setCatalogSortMode,
        applySelectedSkillToProject,
        duplicateSkillToPrivate,
        duplicateSelectedSkillToPrivate,
        shareSkillToOrg,
        unshareSkillToPrivate,
        unshareSelectedSkillToPrivate,
        deleteSelectedSkill,
        saveSelectedSkillContent,
        saveProjectSkills,
        selectSkill
    };
}
